import type { Runway } from "@magicweave/api";

import { useEntitlement } from "../api/hooks";
import { useCurrentOrg } from "./org";
import { useMeterRate, type Meter } from "../api/rates";
import {
  allowanceChip,
  pricedAction,
  type AllowanceChip,
  type PricedAction,
} from "../copy/allowance";

/**
 * The allowance chip, and whether it is still catching up.
 *
 * Read from the same query the plan screen reads, by the same hook and
 * therefore on the same key — so the two of them cost one request between them
 * rather than one each, and cannot show different figures on the same
 * organisation.
 *
 * `settled` is false while an answer for a *different* organisation is on
 * screen. The chip keeps the last answer it had across an org switch instead of
 * emptying itself, because a chip that disappears from the strip is exactly the
 * failure putting it in the strip was meant to fix — and it dims itself
 * meanwhile rather than presenting a figure it has not confirmed.
 */
export function useAllowanceChip(): {
  /** Whose allowance it is, and where the chip leads. */
  orgId: string | undefined;
  chip: AllowanceChip | undefined;
  settled: boolean;
} {
  const orgId = useCurrentOrg();
  const entitlement = useEntitlement(orgId ?? "", { keepPrevious: true });
  const data = entitlement.data;
  return {
    orgId,
    chip: data ? allowanceChip(data) : undefined,
    settled: !entitlement.isPlaceholderData,
  };
}

/**
 * Where the studio stands on its plan's runway — the server's own `runway`,
 * or null when there is nothing to say (no runway, or sign-ups still open).
 *
 * The same reader as the chip: `useCurrentOrg` and `useEntitlement` on the same
 * key, so the runway costs no request of its own and cannot describe a
 * different organisation from the chip beside it. The words are
 * `copy/runway.ts`'s, loaded only with the banner that says them.
 */
export function useRunway(): {
  orgId: string | undefined;
  runway: Runway | null;
  settled: boolean;
} {
  const orgId = useCurrentOrg();
  const entitlement = useEntitlement(orgId ?? "", { keepPrevious: true });
  const runway = entitlement.data?.runway ?? null;
  return {
    orgId,
    runway: runway && runway.state !== "open" ? runway : null,
    settled: !entitlement.isPlaceholderData,
  };
}

/**
 * What one charging control costs, and what the account looks like after it.
 *
 * The same reader as the chip above: `useCurrentOrg` for whose allowance it is
 * and `useEntitlement` for the state of it, on the same cache key, so a control
 * and the chip in the strip cannot show two different accounts or two different
 * walls. There is one place in this console that reads the allowance and this is
 * still it — a second one would be a second answer to "am I refused", which is
 * the disagreement `decisions/0025` decision 6 put the state on the wire to end.
 *
 * `quantity` is the only thing the server could not have derived: the size of
 * the action somebody is looking at but has not taken.
 */
export function usePricedAction(
  meter: Meter,
  quantity: number,
): {
  /** Whose allowance is being spent, and where the ways out are. */
  orgId: string | undefined;
  /**
   * Whole credits, for a surface that wants to say the arithmetic out loud.
   * Undefined until the rate card has arrived — the price comes off the server's
   * active card, so there is a moment before this console knows it.
   */
  cost: number | undefined;
  action: PricedAction;
} {
  const orgId = useCurrentOrg();
  const entitlement = useEntitlement(orgId ?? "");
  const rate = useMeterRate(meter);
  const cost = rate === undefined ? undefined : rate * quantity;

  return { orgId, cost, action: pricedAction(entitlement.data, cost) };
}
